import {
  DynamicFormula,
  DynamicFormulaNode,
  WidgetDynamicType
} from '../widget/dynamic/WidgetDynamic';
import { IProcedure } from '../Procedure';
import { MetadataLogic } from './MetadataLogic';
import { IWidget, WidgetType } from '../widget/Widget';
import { IRequest } from '../request/Request';
import { ContextualValue } from '../contextual-value/ContextualValue';
import { ComputeMath, ComputeMathUtils } from './math/ComputeMath';

export class DynamicComputeMetadataProcessor {
  /**
   * Salva i valori dinamici all'interno dei `values`
   */
  static compile(procedure: IProcedure, request: IRequest) {
    procedure.widgets
      .filter(
        w =>
          w.type === WidgetType.Dynamic &&
          w.dynamic!.type === WidgetDynamicType.Computed
      )
      .forEach(widget => {
        const metadata = MetadataLogic.singleFromWidget(widget);
        const result = this.compileWidget(procedure, request, widget);

        request.values[metadata.id] = result;
      });
  }

  static compileWidget(
    procedure: IProcedure,
    request: IRequest,
    widget: IWidget
  ) {
    if (widget.type !== WidgetType.Dynamic)
      throw new Error('EDM-001 Metadato non valido per la compilazione dinamica.'); // prettier-ignore

    return this.compileAndEvaluateFomula(
      procedure,
      request,
      widget.dynamic!.formula!
    );
  }

  static formulaToMathExpression(formula: DynamicFormula | null) {
    let expression = '';
    let variableCurrentId = 0;
    let variables = {} as Record<string, any>;

    formula?.[0].children.forEach(node => {
      if (DynamicFormulaNode.isMetadata(node)) {
        const variableName = `v${variableCurrentId++}`;
        expression += variableName;
        variables[variableName] = node.metadataId;
        return;
      }

      if (DynamicFormulaNode.isContextual(node)) {
        const variableName = `v${variableCurrentId++}`;
        expression += variableName;
        variables[variableName] = node.contextualValue;
        return;
      }

      if (DynamicFormulaNode.isText(node)) {
        expression += node.text;
        return;
      }

      console.error(`EDM-002 Node: `, node);
      throw new Error('EDM-002 Tipo di campo non gestito per la compilazione dinamica'); // prettier-ignore
    });

    return { expression, variables };
  }

  static compileAndEvaluateFomula(
    procedure: IProcedure,
    request: IRequest,
    formula: DynamicFormula
  ) {
    if (!formula) return '';
    let expression = '';
    let variableCurrentId = 0;
    let variables = {} as Record<string, any>;

    formula[0].children.forEach(node => {
      if (DynamicFormulaNode.isMetadata(node)) {
        const metadata = MetadataLogic.getInProcedure(
          procedure,
          node.metadataId
        );

        let value = request.values[node.metadataId];
        if (metadata.contextual && value == null) {
          const context = ContextualValue.find(metadata.contextual);
          value = context.fromRequest(request) as any;
        }

        const variableName = `v${variableCurrentId++}`;
        expression += variableName;
        variables[variableName] = ComputeMathUtils.prepareMetadataValue(
          value,
          metadata
        );
        return;
      }

      if (DynamicFormulaNode.isContextual(node)) {
        const contextual = ContextualValue.find(node.contextualValue);
        const variableName = `v${variableCurrentId++}`;
        expression += variableName;
        variables[variableName] = ComputeMathUtils.prepareContextualValue(
          contextual.fromRequest(request),
          contextual
        );
        return;
      }

      if (DynamicFormulaNode.isText(node)) {
        expression += node.text;
        return;
      }

      console.error(`EDM-002 Node: `, node);
      throw new Error('EDM-002 Tipo di campo non gestito per la compilazione dinamica'); // prettier-ignore
    });

    try {
      console.log(`[DynamicComputeMetadataProcessor] expression: [${expression}]`, { variables }); // prettier-ignore
      return ComputeMath.evaluate(expression, variables);
    } catch (err) {
      console.error(err, `[DynamicComputeMetadataProcessor] expression: [${expression}]`, { variables }); // prettier-ignore
      return 0;
    }
  }
}
